import { cn } from "@-/com.npmjs.cn";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@-/dev.improve.haniverse.components.ui.tooltip";
import type { PinyinSoundIntroduction } from "@-/dev.improve.haniverse.lib.level";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import {
  type PinyinSound,
  type PinyinTone,
  PINYIN_TONE_NAMES,
  formatPinyinSoundLabel,
  toDiacriticPinyin,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";

/** A sound's first character, resolved for drawing. */
export type PinyinSoundCellCharacter = {
  glyph: GraphemeGlyph;
  introduction: PinyinSoundIntroduction;
  /** Whether the learner has learned the sound; `undefined` until their cards load. */
  isLearned: boolean | undefined;
};

/** Every chart cell is this tall, filled or not, so the grid's rows line up. */
const CELL_CLASS_NAME = "h-14 rounded-xs border";

/**
 * A part of a tone's memory aid: plain text, the word naming the tone's motion (`emphasis`), or
 * the word naming its color (`color`), drawn in that tone's color.
 */
type ToneCaptionPart = string | { emphasis: string } | { color: string };

const TONE_CAPTIONS: Record<PinyinTone, readonly ToneCaptionPart[]> = {
  0: ["A short, light tone with no tone mark."],
  1: ["A high-quality, ", { emphasis: "flat" }, " ", { color: "red" }, " carpet"],
  2: ["A ", { color: "green" }, " plant ", { emphasis: "rising" }, " from the ground"],
  3: [{ emphasis: "Low" }, " ground filled with ", { color: "blue" }, " water"],
  4: [{ color: "Purple" }, " grapes ", { emphasis: "falling" }, " from a grapevine"],
};

function getToneCaptionPartText(part: ToneCaptionPart): string {
  if (typeof part === "string") {
    return part;
  }
  return "emphasis" in part ? part.emphasis : part.color;
}

/** Every caption span is the caption's size: a nested `Text` would otherwise reset to `text-base`. */
const TONE_CAPTION_CLASS_NAME = "text-sm";

const TONE_LABELS: Record<PinyinTone, string> = {
  0: "Neutral Tone",
  1: "1st Tone · High",
  2: "2nd Tone · Rising",
  3: "3rd Tone · Low",
  4: "4th Tone · Falling",
};

/**
 * A full-width tone row with its introduction level above the memory aid, whose color word takes
 * the tone's color (`toneColor`).
 */
export function PinyinToneListItem({
  character,
  onPress,
  tone,
  toneColor,
}: {
  character: PinyinSoundCellCharacter | undefined;
  onPress: (introduction: PinyinSoundIntroduction) => void;
  tone: PinyinTone;
  toneColor: string;
}) {
  const testID = `pinyin-tone-${tone}`;
  const label = TONE_LABELS[tone];
  const captionParts = TONE_CAPTIONS[tone];
  const caption = captionParts.map((part) => getToneCaptionPartText(part)).join("");
  const introductionLabel =
    character === undefined ? null : `Introduced in Level ${character.introduction.levelLabel}`;
  const diacriticPinyin =
    character === undefined ? null : toDiacriticPinyin(character.introduction.pinyin);
  return (
    <Pressable
      accessibilityHint={character === undefined ? undefined : "Shows the level in the list"}
      accessibilityLabel={[
        label,
        introductionLabel,
        caption,
        character === undefined ? null : `${character.introduction.grapheme} ${diacriticPinyin}`,
        character?.isLearned === undefined
          ? null
          : character.isLearned
            ? "Learned"
            : "Not learned yet",
      ]
        .filter(Boolean)
        .join(". ")}
      accessibilityRole="button"
      className="flex-row items-center gap-3 rounded-xs border border-border/60 bg-muted/30 px-3 py-2 active:bg-accent"
      disabled={character === undefined}
      onPress={() => {
        if (character !== undefined) {
          onPress(character.introduction);
        }
      }}
      testID={testID}
    >
      <View className="min-w-0 flex-1 gap-0.5">
        <Text className="text-sm font-semibold">{label}</Text>
        {introductionLabel === null ? null : (
          <Text className="text-xs text-muted-foreground" testID={`${testID}-unlock`}>
            {introductionLabel}
          </Text>
        )}
        <Text className={TONE_CAPTION_CLASS_NAME} testID={`${testID}-caption`}>
          {captionParts.map((part, index) =>
            typeof part === "string" ? (
              part
            ) : "emphasis" in part ? (
              <Text className={cn(TONE_CAPTION_CLASS_NAME, "font-semibold")} key={index}>
                {part.emphasis}
              </Text>
            ) : (
              <Text
                className={TONE_CAPTION_CLASS_NAME}
                key={index}
                style={{ color: toneColor }}
                testID={`${testID}-color-word`}
              >
                {part.color}
              </Text>
            ),
          )}
        </Text>
      </View>
      {character === undefined ? null : (
        <View className="min-w-10 items-center">
          <GraphemeFontGlyph
            className="text-3xl leading-9 text-foreground"
            glyph={character.glyph}
          />
          <Text className="text-xs text-muted-foreground" testID={`${testID}-pinyin`}>
            {diacriticPinyin}
          </Text>
        </View>
      )}
    </Pressable>
  );
}

/** What a screen reader says for a sound: "Initial zh", "Final iang", "Falling tone". */
function getSpokenSoundLabel(sound: PinyinSound): string {
  switch (sound.kind) {
    case "initial": {
      return `Initial ${sound.sound}`;
    }
    case "final": {
      return `Final ${sound.sound}`;
    }
    case "tone": {
      return `${PINYIN_TONE_NAMES[sound.sound]} tone`;
    }
  }
}

/**
 * One chart cell: an initial, final or tone, and the character (with the reading) of the lesson
 * that first teaches it, with the level it unlocks at along the bottom. Pressing it brings that
 * level into view in the list below.
 */
export function PinyinSoundCell({
  character,
  onPress,
  sound,
  testID,
}: {
  /** Undefined while the curriculum's readings load, or when no lesson teaches the sound. */
  character: PinyinSoundCellCharacter | undefined;
  onPress: (introduction: PinyinSoundIntroduction) => void;
  /** The initial, final or tone (`zh-`, `-iang`, the falling tone). */
  sound: PinyinSound;
  testID: string;
}) {
  const label = formatPinyinSoundLabel(sound);
  const spokenLabel = getSpokenSoundLabel(sound);
  const isLearned = character?.isLearned === true;
  const isNotLearned = character?.isLearned === false;
  const unlockLabel =
    character === undefined
      ? null
      : `${isLearned ? "Unlocked" : "Unlocks"} at Level ${character.introduction.levelLabel}`;
  const diacriticPinyin =
    character === undefined ? null : toDiacriticPinyin(character.introduction.pinyin);
  return (
    <Pressable
      accessibilityHint={character === undefined ? undefined : "Shows the level in the list"}
      accessibilityLabel={
        character === undefined
          ? spokenLabel
          : `${spokenLabel}: ${character.introduction.grapheme} ${diacriticPinyin}. ${unlockLabel}${isNotLearned ? ", not learned yet" : ""}`
      }
      accessibilityRole="button"
      className={cn(
        CELL_CLASS_NAME,
        "justify-between border-border/60 bg-muted/30 px-1.5 pt-1 pb-0.5 active:bg-accent",
      )}
      disabled={character === undefined}
      onPress={() => {
        if (character !== undefined) {
          onPress(character.introduction);
        }
      }}
      testID={testID}
    >
      <View className="flex-row items-start justify-between gap-1">
        {/* A tone's name ("Falling") is wider than an initial or final, so it shrinks to fit. */}
        <Text
          adjustsFontSizeToFit={sound.kind === "tone"}
          className={cn(
            "min-w-0 shrink font-semibold text-foreground",
            sound.kind === "tone" ? "text-sm leading-5" : "text-base leading-5",
          )}
          minimumFontScale={0.7}
          numberOfLines={1}
        >
          {label}
        </Text>
        {character === undefined ? null : (
          <View className="items-center">
            <GraphemeFontGlyph
              className="text-2xl leading-7 text-foreground"
              glyph={character.glyph}
            />
            <Text
              className="-mt-0.5 text-[10px] leading-3 text-muted-foreground"
              numberOfLines={1}
              style={character.glyph.color === null ? undefined : { color: character.glyph.color }}
              testID={`${testID}-pinyin`}
            >
              {diacriticPinyin}
            </Text>
          </View>
        )}
      </View>
      {unlockLabel === null ? null : (
        <Text
          adjustsFontSizeToFit
          className="text-[8px] leading-2.5 text-muted-foreground"
          minimumFontScale={0.75}
          numberOfLines={1}
          testID={`${testID}-unlock`}
        >
          {unlockLabel}
        </Text>
      )}
    </Pressable>
  );
}

/**
 * A chart slot no sound fills, like the `ü`-series form of `o`: an empty, disabled cell whose
 * press says why there is nothing there.
 */
export function PinyinSoundGapCell({
  explanation,
  testID,
}: {
  explanation: string;
  testID: string;
}) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger
        accessibilityHint={explanation}
        accessibilityLabel="Empty"
        accessibilityRole="button"
        className={cn(CELL_CLASS_NAME, "border-border/40 bg-muted/60")}
        testID={testID}
      >
        <View />
      </TooltipTrigger>
      <TooltipContent className="max-w-60" side="top">
        <Text>{explanation}</Text>
      </TooltipContent>
    </Tooltip>
  );
}
